import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { first, map, catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AssetInput, Asset } from "../../../management-api-client/model";
import { AssetService, ContractAgreementService } from "../../../management-api-client";
import { AssetEditorDialog } from "../asset-editor-dialog/asset-editor-dialog.component";
import { ConfirmationDialogComponent, ConfirmDialogModel } from "../confirmation-dialog/confirmation-dialog.component";
import { NotificationService, SorterService, UtilService } from "../../services";
import { PageEvent } from '@angular/material/paginator';
import { MetadataDisplayComponent } from '../common/metadata-display/metadata-display.component';
import { METADATA_CONTEXT } from 'src/modules/app/variables';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnauthorizedStateService } from 'src/modules/app/auth/unauthorized-state.service';


@Component({
  selector: 'edc-demo-asset-viewer',
  templateUrl: './asset-viewer.component.html',
  styleUrls: ['./asset-viewer.component.scss']
})
export class AssetViewerComponent implements OnInit, OnDestroy {
  paginationState = {
    filteredList: [] as Asset[],
    pagedList: [] as Asset[],
    pageIndex: 0,
    pageSize: 20
  };
  allAssets: Asset[] = [];
  totalAssets = 0;
  searchText = '';
  isTransferring = false;
  isUnauthorized = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private assetService: AssetService,
    private contractAgreementService: ContractAgreementService,
    private notificationService: NotificationService,
    private readonly dialog: MatDialog,
    private readonly metadataViewDialog: MatDialog,
    private readonly sorterService: SorterService,
    private readonly cdref: ChangeDetectorRef,
    private readonly unauthorizedState: UnauthorizedStateService,
    public readonly utilService: UtilService,
  ) { }

  loadAssets() {
    const { pageIndex, pageSize } = this.paginationState;
    this.assetService.requestAssets({
      limit: pageSize + 1,   // fetch one extra to detect if there's a next page
      offset: pageIndex * pageSize,
    }).subscribe({
      next: (assets: Asset[]) => {
        // If we got pageSize+1 results, there is at least one more page
        const hasMore = assets.length > pageSize;
        const page = hasMore ? assets.slice(0, pageSize) : assets;

        this.paginationState.pagedList = page.sort((a, b) =>
          this.sorterService.naturalSort(
            a.properties.optionalValue('edc', 'name') || a.id,
            b.properties.optionalValue('edc', 'name') || b.id
          )
        );
        // totalAssets drives the paginator length:
        // show at least up to the next page if there are more results
        this.totalAssets = hasMore
          ? (pageIndex + 2) * pageSize
          : pageIndex * pageSize + page.length;
        // keep filteredList in sync (used for paginator [length] binding)
        this.paginationState.filteredList = this.paginationState.pagedList;
      },
      error: (err: HttpErrorResponse) => {
        if (this.unauthorizedState.isUnauthorized('management')) {
          return;
        }
        this.showError(err as any, 'Failed to load assets');
      }
    });
  }

  filterAssets(mainList: Asset[]): Asset[] {
    return mainList.filter(asset =>
      (asset.properties.optionalValue<string>('edc', 'name') || '').toLowerCase().includes(this.searchText.toLowerCase()) ||
      (asset.properties.optionalValue<string>('edc', 'baseUrl') || '').toLowerCase().includes(this.searchText.toLowerCase()) ||
      this.utilService.searchThroughMetadata(this.findMetadataForAsset(asset), this.searchText) ||
      asset.id.toLowerCase().includes(this.searchText.toLowerCase())
    );
  }

  ngOnInit(): void {
    this.unauthorizedState
      .isUnauthorized$('management')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isUnauthorized) => {
        this.isUnauthorized = isUnauthorized;
        if (isUnauthorized) {
          this.allAssets = [];
          this.paginationState.filteredList = [];
          this.paginationState.pagedList = [];
        }
      });

    this.loadAssets();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isBusy() {
    return this.isTransferring;
  }

  onSearch() {
    this.paginationState.pageIndex = 0;
    this.loadAssets();
  }

  onPageChange(event: PageEvent) {
    this.paginationState.pageIndex = event.pageIndex;
    this.paginationState.pageSize = event.pageSize;
    this.loadAssets();
  }

  onDelete(asset: Asset) {
    this.countAgreementsForAsset(asset.id).subscribe(agreementCount => {
      const warning = agreementCount > 0
        ? `This asset is referenced by ${agreementCount} contract agreement(s). The connector will refuse to delete an asset that is part of a negotiated agreement.`
        : undefined;
      const dialogData = ConfirmDialogModel.forDelete("asset", `"${asset.id}"`, warning);
      const ref = this.dialog.open(ConfirmationDialogComponent, {data: dialogData});

      ref.afterClosed().subscribe({
        next: res => {
          if (res) {
            this.assetService.removeAsset(asset.id).subscribe({
              next: () => this.loadAssets(),
              error: err => this.showError(err, this.getDeleteErrorMessage(err)),
              complete: () => this.notificationService.showInfo("Successfully deleted")
            });
          }
        }
      });
    });
  }

  onCreate() {
    const dialogRef = this.dialog.open(AssetEditorDialog);
    dialogRef.afterClosed().pipe(first()).subscribe((result: { assetInput?: AssetInput }) => {
      const newAsset = result?.assetInput;
      if (newAsset) {
        this.assetService.createAsset(newAsset).subscribe({
          next: ()=> this.loadAssets(),
          error: err => this.showError(err, this.utilService.getApiErrorMessage(err) ?? "This asset cannot be created"),
          complete: () => this.notificationService.showInfo("Successfully created"),
        })
      }
    })
  }

  onEdit(asset: Asset) {
    this.countAgreementsForAsset(asset.id).subscribe(agreementCount => {
      const dialogRef = this.dialog.open(AssetEditorDialog, { data: { asset, agreementCount } });
      dialogRef.afterClosed().pipe(first()).subscribe((result: { assetInput?: AssetInput }) => {
        const editedAsset = result?.assetInput;
        if (editedAsset) {
          this.assetService.updateAsset(editedAsset).subscribe({
            next: () => this.loadAssets(),
            error: err => this.showError(err, this.utilService.getApiErrorMessage(err) ?? "This asset cannot be updated"),
            complete: () => this.notificationService.showInfo("Successfully updated"),
          });
        }
      });
    });
  }

  private countAgreementsForAsset(assetId: string): Observable<number> {
    return this.contractAgreementService.queryAllAgreements({
      filterExpression: [{ operandLeft: 'assetId', operator: '=', operandRight: assetId }]
    }).pipe(
      map(agreements => agreements.length),
      // the check is best-effort; do not block the action if the query fails
      catchError(() => of(0))
    );
  }

  private getDeleteErrorMessage(err: any): string {
    const detail = this.utilService.getApiErrorMessage(err);
    if (detail) return detail;
    return err?.status === 409
      ? "The asset cannot be deleted because it is referenced by an existing contract negotiation or agreement."
      : "This asset cannot be deleted";
  }

  private showError(error: string, errorMessage: string) {
    this.notificationService.showError(errorMessage);
    console.error(error);
  }

  onSelect(asset: Asset) {
    const dialogRef = this.metadataViewDialog.open(MetadataDisplayComponent, {
      data: { 
        metadata: this.findMetadataForAsset(asset),
        asset_name: asset.properties.optionalValue<string>('edc', 'name') || asset.id,
      },
    });
    dialogRef.afterClosed().subscribe( );
  }
  
  shouldDisplayMetadata(asset: any) : boolean {
    const metadata = this.findMetadataForAsset(asset);
    if (!metadata || typeof metadata !== 'object') return false;
    return Object.keys(metadata).some(prop => metadata[prop] !== null && metadata[prop] !== undefined);
  }

  findMetadataForAsset(asset: any) {
    // v5beta returns plain "metadata" key; fall back to old expanded JSON-LD key
    return asset.properties?.['metadata']
      ?? asset.properties?.[METADATA_CONTEXT]?.[0]
      ?? null;
  }

  /**
   * Safely compare value to true, handles both boolean and string "true"/"false"
   */
  isAllowed(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return !!value;
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }
}
